<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed, ref } from "vue";
import { Head, usePage } from "@inertiajs/vue3";
import { LIST_SORT_OPTIONS, useListGrid } from "@/composables/useListGrid.js";
import BookmarkCard from "@/Components/Profile/BookmarkCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import ProfileHero from "@/Components/Profile/ProfileHero.vue";
import ProfileTabs from "@/Components/Profile/ProfileTabs.vue";
import PromoBanner from "@/Components/Profile/PromoBanner.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";

// Access props through the page object so in-place reloads (e.g. after the
// Manage Profile modalog saves) aren't read from a stale props reference.
const inertiaPage = usePage();

const owner = computed(() => inertiaPage.props.owner?.data);

// Removals are optimistic: track them locally so the card disappears without
// waiting on a full prop reload.
const removedUuids = ref(new Set());

const bookmarks = computed(() =>
    (inertiaPage.props.bookmarks?.data ?? []).filter(
        (list) => !removedUuids.value.has(list.uuid),
    ),
);

const { sort, page, pages, paged } = useListGrid(bookmarks);

const ownerShortName = computed(() =>
    owner.value.is_organization
        ? owner.value.presentation_name
        : owner.value.first_name,
);

function removeBookmark(list) {
    removedUuids.value = new Set([...removedUuids.value, list.uuid]);

    axios
        .delete(route("bookmark.destroy", { giftlist: list.uuid }))
        .catch((error) => {
            console.error(error);

            const next = new Set(removedUuids.value);
            next.delete(list.uuid);
            removedUuids.value = next;
        });
}
</script>

<template>
    <div>
        <Head>
            <title>Saved Lists | Gift Hero</title>
        </Head>

        <ProfileHero
            :owner="owner"
            :is-owner="true"
            :theme="owner.profile_theme"
        />

        <ProfileTabs :owner="owner" :is-owner="true" active="bookmarks" />

        <div class="px-4 pb-8 sm:px-[35px] sm:pb-9">
            <section
                class="rounded-brand bg-brand-cream px-4 py-6 sm:px-[46px] sm:py-8"
            >
                <header
                    class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
                >
                    <div>
                        <h2
                            class="font-brand text-[25px] font-bold text-[#102a3d]"
                        >
                            {{ ownerShortName }}’s Saved Lists
                        </h2>
                        <p class="mt-1 font-brand text-[15px] text-[#102a3d]">
                            Keep track of wish lists for quick access. Select
                            the
                            <svg
                                viewBox="0 0 12 15"
                                class="inline-block h-[15px] w-3 align-text-bottom text-[#102a3d]"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linejoin="round"
                                aria-label="bookmark"
                                role="img"
                            >
                                <path d="M1 1h10v13L6 10.5 1 14V1z" />
                            </svg>
                            icon while viewing a friend&rsquo;s list!
                        </p>
                    </div>
                    <SortDropdown
                        v-model="sort"
                        :options="LIST_SORT_OPTIONS"
                        class="lg:shrink-0"
                    />
                </header>

                <template v-if="bookmarks.length">
                    <div
                        class="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
                    >
                        <BookmarkCard
                            v-for="list in paged"
                            :key="list.uuid"
                            :list="list"
                            @remove="removeBookmark"
                        />
                    </div>

                    <ListsPagination
                        v-if="pages > 1"
                        v-model:page="page"
                        :pages="pages"
                        class="mt-10"
                    />
                </template>

                <div
                    v-else
                    class="flex flex-col items-center gap-5 py-14 text-center"
                >
                    <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                        You haven&rsquo;t saved any lists yet. Select the
                        bookmark icon while viewing a friend&rsquo;s list to
                        keep it handy here.
                    </p>
                </div>
            </section>

            <PromoBanner
                class="mt-8"
                headline="Need Some
Giftspiration?"
                body="Browse hand-picked gift ideas from our experts and find something they’ll actually love — for any occasion, any budget."
                cta-label="View Expert Lists"
                :cta-href="route('explore')"
                image="images/profile/cta-collage.png"
                mobile-image="images/profile/cta-collage-mobile.png"
                variant="navy"
            />
        </div>
    </div>
</template>
